import { Box, Text } from '@mantine/core';
import { Fragment, type ReactElement } from 'react';
import { type VizAccent } from '@/design/VizAccents';
import type { HeatmapDay } from './InsightsTypes';

interface InsightsContributionHeatmapProps {
    days: HeatmapDay[];
    /** @deprecated Cells are brand-coloured now; accent is ignored. */
    accent?: VizAccent;
    /** The selected window — the calendar is filled across `[startIso, endIso]`, inactive days included. */
    startIso: string;
    endIso: string;
}

const WEEKDAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] as const;
const DAY_MS = 86_400_000;
/** Above this many days, lay the calendar out as month rows (~30/row) instead of week rows. */
const MONTH_MODE_THRESHOLD = 45;
const MONTH_DAY_MARKERS = new Set([1, 8, 15, 22, 29]);

interface FilledDay {
    date: string;
    sessions: number;
}

function cellColor(sessions: number, max: number): string {
    if (sessions === 0) return 'var(--bm-color-stripe)';
    const pct = Math.round((0.14 + 0.76 * (sessions / Math.max(1, max))) * 100);
    return `color-mix(in srgb, var(--bm-color-brand-accent) ${pct}%, transparent)`;
}

function eachDay(startDay: string, endDay: string): string[] {
    const out: string[] = [];
    let t = Date.parse(`${startDay}T00:00:00Z`);
    const end = Date.parse(`${endDay}T00:00:00Z`);
    for (; t <= end; t += DAY_MS) {
        out.push(new Date(t).toISOString().slice(0, 10));
    }
    return out;
}

function addDaysStr(day: string, n: number): string {
    return new Date(Date.parse(`${day}T00:00:00Z`) + n * DAY_MS).toISOString().slice(0, 10);
}

function weekdayOf(day: string): number {
    return new Date(Date.parse(`${day}T00:00:00Z`)).getUTCDay();
}

function monthDayLabel(day: string): string {
    const [, month, date] = day.split('-');
    return `${MONTHS[Number(month) - 1]} ${Number(date)}`;
}

/** Week rows (Sun–Sat), each labeled "Week of {Sunday}", with weekday labels along the bottom. */
function WeekGrid({ filled, max }: { filled: FilledDay[]; max: number }): ReactElement {
    const firstWeekday = filled.length > 0 ? weekdayOf(filled[0]?.date ?? '') : 0;
    const cells: Array<FilledDay | null> = [...Array.from({ length: firstWeekday }, (): null => null), ...filled];
    while (cells.length % 7 !== 0) cells.push(null);
    const weekCount = cells.length / 7;
    const firstSunday = filled[0] ? addDaysStr(filled[0].date, -firstWeekday) : null;

    return (
        <Box style={{ display: 'grid', gridTemplateColumns: 'auto repeat(7, 1fr)', gap: 4, alignItems: 'center' }}>
            {Array.from({ length: weekCount }, (_, week) => {
                const sunday = firstSunday ? addDaysStr(firstSunday, week * 7) : null;
                return (
                    <Fragment key={`week-${String(week)}`}>
                        <Text size="xs" c="dimmed" style={{ fontSize: '9px', whiteSpace: 'nowrap', paddingRight: 8 }}>
                            {sunday ? `Week of ${monthDayLabel(sunday)}` : ''}
                        </Text>
                        {cells.slice(week * 7, week * 7 + 7).map((day, i) =>
                            day === null ? (
                                <Box key={`pad-${String(week)}-${String(i)}`} h={18} />
                            ) : (
                                <Box key={day.date} h={18} style={{ background: cellColor(day.sessions, max), borderRadius: 3 }} />
                            ),
                        )}
                    </Fragment>
                );
            })}
            <Box />
            {WEEKDAY_LABELS.map((label) => (
                <Text key={label} size="xs" c="dimmed" ta="center" style={{ fontSize: '9px' }}>
                    {label}
                </Text>
            ))}
        </Box>
    );
}

/** Month rows, each labeled "Month of {Month}", cells placed by day-of-month (1–31). */
function MonthGrid({ filled, max }: { filled: FilledDay[]; max: number }): ReactElement {
    const byMonth = new Map<string, Map<number, number>>();
    for (const day of filled) {
        const [year, month, date] = day.date.split('-');
        const key = `${year}-${month}`;
        const bucket = byMonth.get(key) ?? new Map<number, number>();
        bucket.set(Number(date), day.sessions);
        byMonth.set(key, bucket);
    }
    const months = [...byMonth.keys()].sort();
    const dayCols = Array.from({ length: 31 }, (_, i) => i + 1);

    return (
        <Box style={{ display: 'grid', gridTemplateColumns: 'auto repeat(31, 1fr)', gap: 3, alignItems: 'center' }}>
            {months.map((key) => {
                const bucket = byMonth.get(key);
                const monthLabel = MONTHS[Number(key.split('-')[1]) - 1];
                return (
                    <Fragment key={key}>
                        <Text size="xs" c="dimmed" style={{ fontSize: '9px', whiteSpace: 'nowrap', paddingRight: 8 }}>
                            Month of {monthLabel}
                        </Text>
                        {dayCols.map((d) =>
                            bucket?.has(d) ? (
                                <Box key={`${key}-${String(d)}`} h={16} style={{ background: cellColor(bucket.get(d) ?? 0, max), borderRadius: 2 }} />
                            ) : (
                                <Box key={`${key}-${String(d)}`} h={16} />
                            ),
                        )}
                    </Fragment>
                );
            })}
            <Box />
            {dayCols.map((d) => (
                <Text key={`marker-${String(d)}`} size="xs" c="dimmed" ta="center" style={{ fontSize: '8px' }}>
                    {MONTH_DAY_MARKERS.has(d) ? d : ''}
                </Text>
            ))}
        </Box>
    );
}

/**
 * Contribution heatmap over the selected window. Inactive days are filled in
 * (not dropped), so the grid spans the real calendar. Short ranges lay out as
 * week rows ("Week of {Sunday}"); long ranges (90d) as month rows ("Month of
 * {Month}", ~30 cells each).
 */
export default function InsightsContributionHeatmap({ days, startIso, endIso }: InsightsContributionHeatmapProps): ReactElement {
    const sessionsByDate = new Map(days.map((d) => [d.date, d.sessions]));
    const filled: FilledDay[] = eachDay(startIso.slice(0, 10), endIso.slice(0, 10)).map((date) => ({
        date,
        sessions: sessionsByDate.get(date) ?? 0,
    }));
    if (filled.length === 0) {
        return <Box />;
    }
    const max = filled.reduce((m, d) => Math.max(m, d.sessions), 0);
    return filled.length > MONTH_MODE_THRESHOLD ? (
        <MonthGrid filled={filled} max={max} />
    ) : (
        <WeekGrid filled={filled} max={max} />
    );
}
